import throttle from 'lodash/throttle';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button } from '@hotelplan/components.common.buttons';
import { optionsWithPassive } from '@hotelplan/libs.polyfills';
import { sx2CssThemeFn } from '@hotelplan/libs.sx';
import { isBrowser } from '@hotelplan/libs.utils';
import { scrollToTop } from 'libs';

const ScrollToTopButtonWrapper = styled.div(
  (props: { show: boolean }) => ({
    display: props.show ? 'block' : 'none',
  }),
  sx2CssThemeFn({
    position: 'fixed',
    zIndex: '3',
    bottom: '10px',
    right: '0px',
  })
);

const ScrollButton = styled(Button).attrs({
  testId: 'scroll-to-top',
  variant: 'iconBtn',
  icon: { name: 'scroll-to-top-icon' },
})(
  sx2CssThemeFn({
    backgroundImage: 'linear-gradient(to right,#fff,#efefef 100%)',
    width: ['30px', '37px'],
    height: ['110px', '72px'],
    fontSize: ['12px', '0px'],
    border: '1px solid #ccc',
    borderRadius: '4px 0 0 4px',
    '&:hover': {
      color: 'primary',
      cursor: 'pointer',
      backgroundImage: 'linear-gradient(to right,#fefefe,#cecece 100%)',
    },
    '.icon': {
      width: ['18px', '23px'],
      height: ['17px', '26px'],
      position: ['absolute', 'static'],
      top: ['13px', 'auto'],
      right: ['5px', 'auto'],
    },
    span: {
      transform: 'rotate(-90deg)',
      position: 'absolute',
      right: '-21px',
      bottom: '35px',
      width: '75px',
    },
  })
);

export default function ScrollToTopButton(): React.ReactElement {
  const [scrollToTopButton, setShowScrollToTopButton] = useState(false);
  const scrollToTopButtonRef = useRef<HTMLDivElement>(null);
  const [t] = useTranslation('common');

  useEffect(() => {
    if (!isBrowser) {
      return;
    }

    const onScroll = throttle(() => {
      setShowScrollToTopButton(window.scrollY > 20);
    }, 150);

    window.addEventListener('scroll', onScroll, optionsWithPassive);

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <ScrollToTopButtonWrapper
      show={scrollToTopButton}
      ref={scrollToTopButtonRef}
    >
      <ScrollButton onClick={() => scrollToTop()}>
        <span>{t('scrollToTop.button')}</span>
      </ScrollButton>
    </ScrollToTopButtonWrapper>
  );
}
